import { sizeClickTracking } from '../GTMEvents';
import { renderFinalSale } from '../FinalSale';
import ConstructorTracking from '../ConstructorTracking';

export class SizeSelector {
  constructor(node, context) {
    this.node = node;
    this.isGiftcard = Boolean(node.dataset.isGiftcard) || false;
    this.priceValue = context?.priceValue || undefined;
    this.formVariantId = context?.formVariantId || undefined;
    this.sizeLabelValue = context?.sizeLabelValue || undefined;
    this.sizes = node.querySelectorAll(
      'a[id^="size_selector_"],a[id^="size_selector_"] + div.pdp-c-atc-size-selector__email-asset',
    );
    this.incentives = context?.incentives || undefined;
    this.activeSize = node.querySelector('.pdp-c-atc-size-selector__size-item--active') || null;
    this.bigCTABtn = document.querySelector('button[class^="pdp-c-atc"][type="submit"]') || null;
    this.productJSONData = context?.productJSONData || {};
    this.priceTag = context?.priceTag || undefined; // Price tag node
    this.constructorTracking = new ConstructorTracking({ isValidCta: true });
  }

  init() {
    // bind events
    this.#bindEvents();
    // trigger almost gone
    // this.activeSize && this.activeSize.dataset.almostGone === 'true' && this.almostGoneMsg.alert();
    this.activeSize;
  }

  #bindEvents() {
    this.sizes.forEach((size) => size.addEventListener('click', this.#handleSizeClick.bind(this)));
  }

  /**
   * Update text content, attributes and classes on Big Call to Action button
   * @param {HTMLAnchorElement} size - size item <a> tag
   * @param {HTMLButtonElement} CTABtn - Big Call to Action button
   */
  #updateBigCTABtn(size, CTABtn) {
    if (size.dataset.availability === 'false') {
      CTABtn.classList.add('pdp-c-atc__submit-btn--waitlist');
      CTABtn.querySelector('span').textContent = 'Join Waitlist';
      CTABtn.dataset.waitlistOpen = 'true';
    } else {
      CTABtn.classList.remove('pdp-c-atc__submit-btn--waitlist');
      CTABtn.classList.remove('pdp-c-atc__submit-btn--soldout');
      CTABtn.querySelector('span').textContent = 'Add to Bag';
      CTABtn.dataset.waitlistOpen = 'false';
    }
  }

  #updateFinalSale() {
    renderFinalSale(window.finalSaleResults);
  }

  /**
   * Update other elements on the right side of PDP on gift cards products, such as prices, pdp-incentives.
   * @param {HTMLAnchorElement} size - size <a> tag element
   */
  #updateGiftcardFeatureFlag(size) {
    const price = Number(size.textContent.replace('$', ''));
    this.priceValue.textContent = size.textContent.trim() + '.00';
    this.priceValue.dataset.price = price * 100;

    // Update incentives
    if (price >= 75) {
      this.incentives.textContent = 'This Item Ships Free';
    } else {
      this.incentives.textContent = 'Free shipping on orders over $75';
    }
  }

  /**
   * Dispatch custom event for Klarna messaging update when variant changes
   * @param {HTMLAnchorElement} sizeItem - size item <a> tag element with variant data
   */
  #dispatchVariantChangeEvent(sizeItem) {
    const productScript = document.querySelector('[data-product-json]');
    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        const variant = productData.variants.find((v) => v.id.toString() === sizeItem.dataset.variantId);
        if (variant) {
          document.dispatchEvent(
            new CustomEvent('pdp:variantChanged', {
              detail: { variant },
            }),
          );
        }
      } catch (e) {
        console.warn('[SizeSelector] Failed to dispatch variant change event:', e);
      }
    }
  }

  /**
   * Update regular price, sale price, and markdown discount when users shift between size swatches
   * @param {string} stringPrice - price that comes from Shopify server
   * @param {string} stringCompareAtPrice - compare at price that comes from Shopify server
   * @returns {void} - nothing
   */
  #updatePricing(stringPrice, stringCompareAtPrice) {
    if (!stringPrice && !stringCompareAtPrice) return;

    const price = Number(stringPrice) / 100;
    const compareAtPrice = Number(stringCompareAtPrice) / 100;
    const markdownDiscount = Math.floor(((compareAtPrice - price) / compareAtPrice) * 100);
    const currencySymbol =
      window.theme.currency_symbol && window.theme.currency_iso !== 'USD' ? window.theme.currency_iso : '$';

    this.priceTag.ariaLabel = `Price: ${currencySymbol}${price.toFixed(2)} ${
      markdownDiscount ? `Original price: ${currencySymbol}${compareAtPrice.toFixed(2)}, ${markdownDiscount}% off` : ''
    }`;
    this.priceTag.dataset.price = stringPrice;
    this.priceTag.dataset.compareAtPrice = stringCompareAtPrice;
    this.priceTag.innerHTML = `
      <span class="pdp-c-atc-price-tag__regular" data-price="${stringPrice}">${currencySymbol}${price.toFixed(2)}</span>
      ${markdownDiscount ? `<span class="pdp-c-atc-price-tag__sale">${compareAtPrice.toFixed(2)}</span>` : ''}
      ${markdownDiscount ? `<span class="pdp-c-atc-price-tag__discount">${markdownDiscount}% off</span>` : ''}
    `;
  }

  /**
   * Handler for any size clicking
   * @param {Event} evt - size event
   */
  #handleSizeClick(evt) {
    // Catch size item when it is clicked
    let sizeItem = evt.target;
    if (sizeItem.classList.contains('pdp-c-atc-size-selector__email-asset')) {
      sizeItem = evt.target.previousElementSibling;
    }

    // Change active size in the list and into data object
    this.sizes.forEach((size) => size.classList.remove('pdp-c-atc-size-selector__size-item--active'));
    sizeItem.classList.toggle('pdp-c-atc-size-selector__size-item--active');
    this.activeSize = sizeItem;

    // update input "id" field into form
    this.formVariantId.value = sizeItem.dataset.variantId;
    // update either Add to Bag or Waitlist button
    this.#updateBigCTABtn(sizeItem, this.bigCTABtn);

    // update size label value
    this.sizeLabelValue.textContent = sizeItem.textContent;
    // update regular price and incentives when product is a gift-card or GIVEX
    this.isGiftcard && this.#updateGiftcardFeatureFlag(sizeItem);

    // update price tag node on regular products
    // Edge-case: Some size variants could have had different prices and compare at prices.
    // Therefore, it had better re-render price tag, in cases prices and compare at prices are different
    const isPriceDifferent =
      sizeItem.dataset.price !== this.priceTag.dataset.price ||
      sizeItem.dataset.compareAtPrice !== this.priceTag.dataset.compareAtPrice;

    !this.isGiftcard &&
      isPriceDifferent &&
      this.#updatePricing(sizeItem.dataset.price, sizeItem.dataset.compareAtPrice);

    // Update final Sale label
    this.#updateFinalSale();

    // Defer this block because they are not user-visible tasks
    setTimeout(() => {
      // Trigger almost gone message whenever size qualifies for almost gone alert
      // sizeItem.dataset.almostGone === 'true' && this.almostGoneMsg.alert();
      // Tracking event and update PDP url
      sizeClickTracking(sizeItem.textContent.trim());
      history.replaceState(null, null, window.location.pathname + `?variant=${sizeItem.dataset.variantId}`);

      // Dispatch custom event for Klarna messaging update
      this.#dispatchVariantChangeEvent(sizeItem);
      this.constructorTracking.updateVariation();
    }, 0);
  }
}
